import { TradingPost, TradingSearchParams } from '../types/TradingTypes';

// Cache for trading data to avoid repeated fetches
let tradingCache: TradingPost[] | null = null;

/**
 * Fetches all trading posts from the JSON file
 */
export const fetchTradingPosts = async (): Promise<TradingPost[]> => {
  // Return cached data if available
  if (tradingCache) {
    return tradingCache;
  }

  try {
    const response = await fetch('/tradingData.json');
    if (!response.ok) {
      throw new Error('Failed to fetch trading data');
    }
    
    const data = await response.json();
    
    // Sort posts by date (newest first)
    const sortedPosts = data.sort((a: TradingPost, b: TradingPost) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Cache the sorted data
    tradingCache = sortedPosts;
    
    return sortedPosts;
  } catch (error) {
    console.error('Error fetching trading posts:', error);
    return [];
  }
};

/**
 * Gets a single trading post by ID
 */
export const getTradingPostById = async (id: string): Promise<TradingPost | null> => {
  const posts = await fetchTradingPosts();
  return posts.find(post => post.id === id) || null;
};

/**
 * Searches trading posts based on query parameters
 */
export const searchTradingPosts = async (params: TradingSearchParams): Promise<TradingPost[]> => {
  const posts = await fetchTradingPosts();
  
  return posts.filter(post => {
    // Filter by search query
    if (params.query) {
      const query = params.query.toLowerCase();
      const inTitle = post.title.toLowerCase().includes(query);
      const inContent = post.content.toLowerCase().includes(query);
      const inSummary = post.summary.toLowerCase().includes(query);
      const inAuthor = post.author.toLowerCase().includes(query);
      const inTrades = post.trades.some(trade => 
        trade.symbol.toLowerCase().includes(query) ||
        (trade.notes && trade.notes.toLowerCase().includes(query))
      );
      
      if (!(inTitle || inContent || inSummary || inAuthor || inTrades)) {
        return false;
      }
    }
    
    // Filter by tag
    if (params.tag && !post.tags.includes(params.tag)) {
      return false;
    }
    
    // Filter by symbol
    if (params.symbol && !post.trades.some(trade => trade.symbol === params.symbol)) {
      return false;
    }
    
    return true;
  });
};

/**
 * Gets all unique tags from trading posts
 */
export const getAllTradingTags = async (): Promise<string[]> => {
  const posts = await fetchTradingPosts();
  
  // Extract all tags and remove duplicates
  const allTags = posts.flatMap(post => post.tags);
  return [...new Set(allTags)].sort();
};

/**
 * Gets all unique symbols from trading posts
 */
export const getAllTradingSymbols = async (): Promise<string[]> => {
  const posts = await fetchTradingPosts();
  
  // Extract all symbols and remove duplicates
  const allSymbols = posts.flatMap(post => post.trades.map(trade => trade.symbol));
  return [...new Set(allSymbols)].sort();
};

/**
 * Formats a date string for display
 */
export const formatTradingDate = (dateString: string): string => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}; 